import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';

import { useLazyApi } from 'Shared/services/Api';
import { usePolicyContext } from 'Policies/utils/PolicyContext/PolicyContext';
import { UpdatePolicyCancellationDraftResponseType } from '@clearcover/core-app-types';

/**
 * Hook to handle calling customer initiated cancellation endpoints; create, update, and apply.
 * @param setCancelPolicyDraftId function that takes the draft id as param to be used outside the hook
 */
export const useCancellation: (
  setCancelPolicyDraftId: (draftId: string) => void
) => {
  data: UpdatePolicyCancellationDraftResponseType;
  updateCancellationDraft: (
    reason?: string | undefined,
    isoDate?: string | undefined
  ) => Promise<void | AxiosResponse<UpdatePolicyCancellationDraftResponseType>>;
  applyCancelPolicy: (
    cancellationReason: string,
    cancellationDate: string
  ) => Promise<void | AxiosResponse<{}>>;
  terminatePolicyRenewal: () => Promise<void | AxiosResponse<{}>>;
  loading: boolean;
  errorMessage: string;
} = (setCancelPolicyDraftId) => {
  const { id: policyId } = useParams<{ id: string }>();
  const {
    details: { timeZone },
  } = usePolicyContext();

  const [
    { draftId, totalAmountDue, summary, cancellationNotice },
    setCancellationData,
  ] = useState<UpdatePolicyCancellationDraftResponseType>({
    draftId: '',
    totalAmountDue: 0,
    summary: [],
  });

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [
    { data: updatedCancelPolicyData, loading: updatedEffectiveDateLoading },
    updateCancellationEffectiveDate,
  ] = useLazyApi<UpdatePolicyCancellationDraftResponseType>({
    method: 'POST',
    url: `/policies/draft/cancellation/update`,
  });
  useEffect(() => {
    if (updatedCancelPolicyData) {
      setCancelPolicyDraftId(updatedCancelPolicyData.draftId);
      setCancellationData(updatedCancelPolicyData);
      setErrorMessage('');
    }
  }, [updatedCancelPolicyData, setCancelPolicyDraftId]);

  const updateCancellationDraft = useCallback(
    (
      cancellationReason: string = '',
      isoDate: string = DateTime.fromJSDate(new Date(), {
        zone: timeZone,
      }).toISO()
    ) =>
      updateCancellationEffectiveDate({
        data: {
          policyId,
          draftId,
          cancellationReason,
          effectiveDate: DateTime.fromISO(isoDate, {
            zone: timeZone,
          })
            .startOf('day')
            .toISO(),
        },
      }).catch(({ message }) => {
        setErrorMessage(message);
      }),
    [updateCancellationEffectiveDate, policyId, draftId, timeZone]
  );

  const [, applyCancelPolicyCall] = useLazyApi<{}>({
    method: 'POST',
    url: `/policies/draft/cancellation/apply`,
  });

  const applyCancelPolicy = useCallback(
    (cancellationReason: string, cancellationDate: string) =>
      applyCancelPolicyCall({
        data: {
          policyId,
          cancellationReason,
          cancellationDate,
          draftId,
        },
      }).catch(({ message }) => {
        setErrorMessage(message);
      }),
    [applyCancelPolicyCall, policyId, draftId]
  );

  const [, terminatePolicyRenewalCall] = useLazyApi<{}>({
    method: 'GET',
    url: `/policies/${policyId}/renewal/terminate`,
  });

  const terminatePolicyRenewal = useCallback(
    () =>
      terminatePolicyRenewalCall().catch(({ message }) =>
        setErrorMessage(message)
      ),
    [terminatePolicyRenewalCall]
  );

  return {
    data: {
      draftId,
      summary,
      totalAmountDue,
      cancellationNotice,
    },
    updateCancellationDraft,
    terminatePolicyRenewal,
    applyCancelPolicy,
    loading: updatedEffectiveDateLoading,
    errorMessage,
  };
};

export const getLatestDate = (date: DateTime, otherDate: DateTime) =>
  date.diff(otherDate).milliseconds < 0 ? otherDate : date;
